import { Directive, Input } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { SplitButtonElement } from '@app/components/elements/splitbutton/splitbutton.component';
import { ELEMENT_PROVIDER } from '@app/directives/splitbutton-elements-collector/splitbutton-elements-collector.directive';
import { WorkflowService } from '@workflow/services/workflow.service';
import { ParticipantDto } from '@api/projects/interfaces';
import { map, switchMap } from 'rxjs/operators';
import { PermissionService } from '@app/services/permission/permission.service';
import { CustomStateTransition, IWorkflow, IWorkflowItem } from '@interfaces/interfaces';
import { ConfirmDialog } from '@app/services/confirm-dialog/confirm-dialog.service';
import { TranslateService } from '@ngx-translate/core';
import { capitalize, flatten } from 'lodash';

interface IWFItemTransitionRecord {
	itemId: string;
	transition: CustomStateTransition;
}
interface IWFItemParticipantsOnTransition {
	relevantRecord: IWFItemTransitionRecord;
	relevantParticipants: ParticipantDto[];
}

function collectBulkActions(items: IWorkflowItem[]): IWFItemTransitionRecord[]{
	return flatten(
		items.map(({id, customStateTransitions}) =>
			(customStateTransitions ?? [])
				.filter(t => t.isBulkActionPossible)
				.map(transition => ({itemId: id, transition}))
		)
	);
}

@Directive({
	selector: '[appBulkActionsProvider]',
	exportAs: 'appBulkActionsProvider',
	providers: [{
		provide: ELEMENT_PROVIDER,
		useFactory: (instance: SplitButtonBulkProviderDirective) => instance.bulkSplitButtonElements$,
		deps: [SplitButtonBulkProviderDirective],
		multi: true,
	}]
})
export class SplitButtonBulkProviderDirective {
	
	private selectedParticipants$ = new BehaviorSubject<ParticipantDto[]>([]);
	
	@Input() set appBulkActionsProvider(participants: ParticipantDto[]) { this.selectedParticipants$.next(participants); }
	
	public readonly bulkSplitButtonElements$: Observable<SplitButtonElement[]>;
	
	constructor(
		private confirmDialog: ConfirmDialog,
		private translateService: TranslateService,
		private workflowService: WorkflowService,
		permissionService: PermissionService,
	) {
		const cTransitionsWithPermission = workflowService.workflow$.pipe(
			map(wf => collectBulkActions(wf?.items ?? [])),
			switchMap((items) => 
				items.length > 0 ? 
					combineLatest(
						items.map(record => permissionService.hasAnyPermissions$(...(record.transition.requiredPermissions ?? [])).pipe(map(p => p ? [record] : []))),
					) :
					of<IWFItemTransitionRecord[][]>([])
			),
			map(flatten)
		);
		
		this.bulkSplitButtonElements$ = combineLatest([
			cTransitionsWithPermission,
			this.selectedParticipants$,
			workflowService.workflow$,
		]).pipe(
			map(([transitions, participants, workflow]) => this.buildSplitButtons(transitions, participants, workflow)),
		);
	}
	
	private buildSplitButtons(transitionRecords: IWFItemTransitionRecord[], participants: ParticipantDto[], workflow: IWorkflow): SplitButtonElement[] {
		// wir bauen einen Button für jeden wfItem-Schritt mit Transition
		// ... dieser taucht immer auf:
		//    * wenn kein participant in diesem wfItem eine state transition hat, dann ausgegraut
		//    * wenn participants vorhanden sind, dann als aktiver Button
		// ... d.h. - eine Transition ist stets relevant
		
		const relevantParticipantsOnRecords: IWFItemParticipantsOnTransition[] = transitionRecords
			.map(record => {
				return {
					relevantRecord: record,
					relevantParticipants: participants.filter(p => record.transition.startStateId === p.workflowStatus?.status[record.itemId]?.customState)
				} as IWFItemParticipantsOnTransition;
			});
		
		return relevantParticipantsOnRecords.map(participantsOnRecord => {
			const relevantWorkflowItem = workflow.items.find(item => item.id === participantsOnRecord.relevantRecord.itemId);
			const relevantCustomState = relevantWorkflowItem?.customStates?.find(state => state.id === participantsOnRecord.relevantRecord.transition.endStateId);
			
			const numberOfParticipantsText = participantsOnRecord.relevantParticipants.length ? `${participantsOnRecord.relevantParticipants.length}x ` : '';
			
			return {
				title: `${numberOfParticipantsText}${capitalize(participantsOnRecord.relevantRecord.transition.label)} (${relevantWorkflowItem.shortName ?? relevantWorkflowItem.name})`,
				icon: relevantCustomState?.symbol || null,
				iconType: 'nzicon',
				type: 'default',
				disabled: participantsOnRecord.relevantParticipants.length === 0,
				callback: () => this.doTransition(
					participantsOnRecord,
					participantsOnRecord.relevantParticipants.map(p => p.id), 
					[participantsOnRecord.relevantRecord.itemId]
				),
			} as SplitButtonElement;
		}).filter(btn => !!btn);
	}
	
	private async doTransition(participantsOnRecord: IWFItemParticipantsOnTransition, participantIds: string[], itemIds: string[]): Promise<void> {
		try {
			if (await this.confirm(participantsOnRecord.relevantRecord, participantIds.length)) {
				this.workflowService.bulkSwitchToState$(participantsOnRecord.relevantRecord.transition, participantIds, itemIds).catch(console.log);
			}
		} catch (error) {
			console.error(error);
		}
	}
	
	private async confirm(transitionRecord: IWFItemTransitionRecord, numParticipants: number): Promise<boolean>{
		return await this.confirmDialog.prompt({
			title: await this.translateService.get('workflow.modal.transition.bulk.titel').toPromise(),
			text: await this.translateService.get('workflow.modal.transition.bulk.text', {transitionAction: transitionRecord.transition.label, num: numParticipants}).toPromise(),
			yesText: await this.translateService.get(transitionRecord.transition.label).toPromise(),
		});
	}
}
